import React, { useMemo, useCallback } from 'react'
import { withRouter } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Row, Col, Spin } from 'antd'
import { useAiCreditCosts } from './hooks'
import TopCostUsers from './components/TopCostUsers'
import ErrorState from './components/ErrorState'
import Unauthorized from '../Training/View/pages/Unauthorized'
import './AiCreditCostReport.scss'
import NewHeader from '../../components/NewHeader'
import ChevronRightIcon from '../../assets/icons/chevron-right-icon.svg'
import ReportsIcon from '../../assets/icons/reports-icon.svg'
import moment from 'moment'
import { useSelector } from 'react-redux'

const AiCreditCostReport = ({ history }) => {
  const { t } = useTranslation()

  // Date filter, search, and user data management for Costs only
  const {
    tableFilter: costTableFilter,
    setTableFilter: setCostTableFilter,
    userData,
    userLoading,
    userError,
    pagination,
    searchTerm,
    onSearch,
    onPageChange,
    refetchUsers,
    selectedType,
    setSelectedType,
    selectedOrg,
    setSelectedOrg,
    orgList
  } = useAiCreditCosts('5')

  const activeTypes = useMemo(() => [
    'ai-image',
    'ai_video',
    // 'audio',
    'course',
    'podcast',
    'maze',
    'sharebot'
  ], [])

  const loading = userLoading
  const error = userError

  const isTableActionLoading = React.useRef(false)

  React.useEffect(() => {
    if (!userLoading) {
      isTableActionLoading.current = false
    }
  }, [userLoading])

  const refetch = useCallback(() => {
    refetchUsers()
  }, [refetchUsers])

  const formatModelName = (modelString) => {
    if (!modelString) return ''
    return modelString
      .replace(/-/g, ' ')
      .replace(/\bpreview\b/gi, '')
      .replace(/\blatest\b/gi, '')
      .trim()
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const handleCostsExportClick = useCallback(() => {
    if (!userData || userData.length === 0) return

    const isSharebot = selectedType === 'sharebot'

    let avgCostTitle = 'Average Cost'
    if (selectedType === 'ai-image') avgCostTitle = 'Average Cost (per image)'
    else if (selectedType === 'ai_video') avgCostTitle = 'Average Cost (per video)'
    else if (selectedType === 'podcast') avgCostTitle = 'Average Cost (per second)'
    else if (selectedType === 'maze') avgCostTitle = 'Average Cost (per depth)'
    else if (selectedType === 'course') avgCostTitle = 'Average Cost (per page)'
    else if (selectedType === 'sharebot') avgCostTitle = 'Average Cost (per question)'

    // Prepare CSV header
    const header = ['Rank', 'User', 'Email']
    if (!isSharebot) {
      header.push(selectedType === 'ai-image' ? 'Model Used' : 'Title')
    }

    header.push('Organization')
    if (selectedType === 'podcast') {
      header.push('Duration (seconds)')
    }

    if (selectedType === 'course') {
      header.push('Total Pages')
    }

    if (selectedType === 'maze') {
      header.push('Depth')
    }

    header.push(avgCostTitle, 'Total Cost')
    if (selectedType === 'course') {
      header.push('Elearning Course')
    }
    // Prepare CSV data rows
    const rows = userData.map((row, index) => {
      const rank = (pagination?.current - 1) * pagination?.pageSize + index + 1
      const rowData = [
        rank,
        row.name || 'Unknown User',
        row.email || ''
      ]

      if (!isSharebot) {
        rowData.push(selectedType === 'ai-image' ? formatModelName(row.model) : (row.title || ''))
      }

      rowData.push(row.organization)
      if (selectedType === 'podcast') {
        rowData.push(row.duration)
      }

      if (selectedType === 'course') {
        rowData.push(row.totalPages)
      }

      if (selectedType === 'maze') {
        rowData.push(row.depth)
      }

      rowData.push(row.totalCreditsUsed || 0)
      rowData.push(row.totalCost || 0)
      if (selectedType === 'course') {
        rowData.push(row.elearningCourse)
      }
      return rowData
    })

    // Combine header and rows
    const csvContent = [header, ...rows]
      .map(e => e.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n')

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const fileName = `ai_cost_usage_${selectedType || 'report'}_${moment().format('YYYY-MM-DD')}.csv`

    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [userData, pagination, selectedType])

  const handleRetry = useMemo(
    () => () => {
      refetch()
    },
    [refetch]
  )

  // Determine view state
  const viewState = useMemo(() => {
    if (loading && (!userData || userData.length === 0)) return 'loading'
    if (error && (!userData || userData.length === 0)) return 'error'
    return 'success'
  }, [userData, error, loading])

  // Render based on state
  const renderContent = () => {
    if (viewState === 'loading' && !isTableActionLoading.current) {
      return (
        <div className="ai-credit-loading-container">
          <Spin size="large" />
        </div>
      )
    }

    if (viewState === 'error') {
      if (error && (String(error).includes('403') || String(error).toLowerCase().includes('access denied'))) {
        return <Unauthorized header={false} />
      }
      return <ErrorState error={error} onRetry={handleRetry} />
    }

    return (
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <TopCostUsers
            title="Top AI Users (Cost)"
            data={{ topUsers: userData || [] }}
            loading={userLoading}
            onExportClick={handleCostsExportClick}
            onSearch={(term) => {
              isTableActionLoading.current = true
              onSearch(term)
            }}
            searchTerm={searchTerm}
            pagination={pagination}
            onPageChange={(page, pageSize) => {
              isTableActionLoading.current = true
              onPageChange(page, pageSize)
            }}
            tableFilter={costTableFilter}
            onTableFilterChange={(filter) => {
              isTableActionLoading.current = true
              setCostTableFilter(filter)
            }}
            activeTypes={activeTypes}
            selectedType={selectedType}
            onTypeChange={(type) => {
              isTableActionLoading.current = true
              setSelectedType(type)
            }}
            orgList={orgList}
            selectedOrg={selectedOrg}
            onOrgChange={(org) => {
              isTableActionLoading.current = true
              setSelectedOrg(org)
            }}
          />
        </Col>
      </Row>
    )
  }

  const goBack = () => {
    history.push('/reports')
  }

  return (
    <div className="ai-credit-usage-report">
      <div className="ai-credit-nav-header">
        <div className="ai-credit-usage-report-breadcrumbs">
          <button className="breadcrumb-home" onClick={() => goBack()}>
            <img src={ReportsIcon} alt="Home" width="20" height="20" />
            Reports
          </button>
          <span className="breadcrumb-sep">
            <img src={ChevronRightIcon} alt=">" width="24" height="24" />
          </span>
          <span className="breadcrumb-settings">
            Ai Cost Usage
          </span>
        </div>
        <div className="ai-credit-usage-report-newheader">
          <NewHeader />
        </div>
      </div>
      <div className="report-header">
        <div className="header-content">
          <div className="title-section">
            <h1 className="main-title">AI Cost Usage</h1>
            <p className="subtitle">
              Real-time visibility into AI cost usage at the organization level
            </p>
          </div>
        </div>
      </div>

      <div className="report-body">
        {renderContent()}
      </div>
    </div>
  )
}
export default withRouter(AiCreditCostReport)
